// ---------------------------------------------------------------------------
// Client bootstrap: join dialog → websocket → render loop with 2D/3D toggle.
// ---------------------------------------------------------------------------

import { getDef } from "../shared/defs";
import { shortId } from "../shared/math";
import "./account-ui"; // accounts: login + settings/deck sync (top-bar slot)
import "./import-ui"; // deck importing (listens for the tt-import-deck event)
import { actionsFor } from "./actions";
import { Input } from "./input";
import { showLobby } from "./lobby";
import { Net } from "./net";
import { Replica } from "./replica";
import { Render2D } from "./render2d";
import { Render3D } from "./render3d";
import type { Renderer } from "./renderer";
import { Settings } from "./settings";
import { materialOf, Sound } from "./sound";
import { UI } from "./ui";

const root = document.getElementById("app")!;
const gameEl = document.getElementById("game")!;

const settings = new Settings();
// Decoupled feature modules (account sync, import) reach shared services here.
(window as any).tt = { settings };
const ui = new UI(root, settings);
const state = new Replica();

// Room id comes from the URL path (/myroom) or ?room=; "/" shows the lobby.
function roomFromUrl(): string | null {
  const q = new URLSearchParams(location.search).get("room");
  if (q) return q.toLowerCase();
  const path = location.pathname.replace(/^\/+|\/+$/g, "");
  return path ? path.toLowerCase() : null;
}

// Stable per-browser player id so refreshes keep your seat.
function playerId(): string {
  let pid = localStorage.getItem("tt.pid");
  if (!pid) {
    pid = shortId();
    localStorage.setItem("tt.pid", pid);
  }
  return pid;
}

async function boot(): Promise<void> {
  const room = roomFromUrl();
  if (!room) {
    showLobby(root); // "/" is the table browser — picking one navigates away
    return;
  }
  // A fresh table from the lobby arrives as /<id>?new=<type>; the setup type
  // rides along on the first websocket connect, then the URL is cleaned.
  const setup = new URLSearchParams(location.search).get("new") ?? undefined;
  if (setup) {
    // Drop only ?new — other params (?bench) must survive the clean.
    const rest = new URLSearchParams(location.search);
    rest.delete("new");
    const qs = rest.toString();
    history.replaceState(null, "", `/${room}${qs ? `?${qs}` : ""}`);
  }

  // ?bench=N skips the join dialog and prints avg frame cost over N frames —
  // lets a one-shot headless screenshot measure renderer performance.
  const benchRaw = new URLSearchParams(location.search).get("bench");
  const bench = benchRaw !== null;
  const benchFrames = Math.max(10, Number(benchRaw) || 150);

  const savedName = localStorage.getItem("tt.name") ?? "";
  const name = bench ? "Bench" : await ui.askName(savedName);
  if (!bench) localStorage.setItem("tt.name", name);

  // Procedural sound fx: server fx messages + observed replica transitions.
  const sound = new Sound(() => ({ on: settings.data.soundOn, volume: settings.data.soundVolume }));
  sound.init();
  state.onSfx = (kind, def) => sound.play(kind, materialOf(def));
  window.addEventListener("tt-test-sound", () => sound.play("stack"));

  // Renderers: 3D is the default, 2D one keypress away.
  let renderer: Renderer = new Render3D(settings);
  renderer.mount(gameEl);
  ui.setMode("3d");

  // Shadow/sharpness changes need a renderer rebuild; do it seamlessly.
  let lastGraphics = `${settings.data.shadowQuality}|${settings.data.renderScale}`;
  settings.onChange(() => {
    const now = `${settings.data.shadowQuality}|${settings.data.renderScale}`;
    if (now === lastGraphics) return;
    lastGraphics = now;
    const next: Renderer = renderer.mode === "3d" ? new Render3D(settings) : new Render2D(settings);
    renderer.unmount();
    renderer = next;
    renderer.mount(gameEl);
    renderer.focusSeat(state.seat);
  });

  const net = new Net(room, name, playerId(), setup, (msg) => {
    state.apply(msg);
    if (msg.t === "welcome") {
      renderer.focusSeat(state.seat);
      // Per-game action bar (welcome re-arrives on table swaps too).
      ui.setActions(actionsFor(state.table.type, {
        send: (i) => net.send(i),
        state,
        ui,
        settings,
        targets: () => {
          if (state.selected.size > 0) return [...state.selected];
          return input.hoveredId ? [input.hoveredId] : [];
        },
      }));
      // A table with hand zones, none of them yours → you joined as a watcher.
      const handSeats = [...state.objs.values()].filter((o) => o.def === "zone.hand");
      if (handSeats.length > 0 && !handSeats.some((o) => o.seat === state.seat)) {
        ui.toast("Player seats are full — you're spectating (you can still move things and chat)");
      }
    }
    if (msg.t === "upsert") {
      // Adopt objects the server just put in our grip (deck pulls).
      for (const o of msg.objs) {
        if (o.heldBy === state.you) input.adoptHold(o.id);
      }
    }
    if (msg.t === "error") input.cancelDrag();
    if (msg.t === "fx") sound.play(msg.kind); // shuffle riffle / dice rattle / deal
    if (msg.t === "chat" && msg.pid !== state.you) sound.play("stack", "die"); // a soft ping for others' messages
    if (msg.t === "inspect") {
      const o = state.objs.get(msg.id);
      const isCardStack = o ? getDef(o.def).acceptsGroup?.startsWith("cards") ?? false : false;
      ui.showInspector(
        msg.id,
        o ? getDef(o.def).name : "Stack",
        msg.items,
        (index, dest) => net.send({ t: "takeAt", id: msg.id, index, dest }),
        (index, ev) => input.startInspectorDrag(msg.id, index, ev),
        // Face-down card decks default to shuffling when the view closes.
        isCardStack && !(o?.faceUp ?? true),
        (shuffle) => {
          net.send({ t: "inspectClose", id: msg.id });
          const still = state.objs.get(msg.id);
          if (shuffle && still && getDef(still.def).kind === "stack") {
            net.send({ t: "shuffle", id: msg.id });
          }
        },
      );
    }
    if (msg.t === "scryView") {
      ui.showScryPanel(msg.mode, msg.items, (keepTop, away) =>
        net.send({ t: "scryResolve", id: msg.id, mode: msg.mode, keepTop, away }),
      );
    }
    if (msg.t === "revealView") {
      ui.showRevealPanel(msg.who, msg.items);
    }
    if (msg.t === "gameState") {
      gameState = { life: msg.life, cmd: msg.cmd };
    }
    if (msg.t === "savedObjects") {
      // Thumbnail: the primary saved object (top card of a stack if any).
      const first = msg.data.objects[0] as { def?: string; contents?: Array<{ def: string }> } | undefined;
      const thumbDef = first?.contents?.length ? first.contents[first.contents.length - 1]!.def : first?.def;
      settings.data.bags.unshift({ name: msg.name, at: Date.now(), thumbDef, data: msg.data });
      if (settings.data.bags.length > 40) settings.data.bags.pop();
      settings.save();
      ui.toast(`Saved "${msg.name}" to your bag`);
    }
    if (msg.t === "remove") {
      for (const id of msg.ids) ui.closeInspector(id);
    }
    if (msg.t === "upsert") {
      // A collapsed container (deck → lone card) invalidates its inspector.
      for (const o of msg.objs) {
        if (ui.openInspectorId === o.id && getDef(o.def).kind !== "stack") ui.closeInspector(o.id);
      }
    }
  }, (connected) => ui.setStatus(connected));

  const input = new Input(gameEl, () => renderer, state, net, ui, settings);
  ui.onChatSend = (m) => net.send({ t: "chat", m });

  // Debug/driving hooks (used by headless verification; server validates all).
  Object.assign((window as any).tt, { state, send: (i: unknown) => net.send(i as Parameters<Net["send"]>[0]) });

  // Perf harness (used headless where rAF barely ticks): average ms for one
  // simulated frame of replica tick + render, over n frames.
  (window as any).tt.bench = (n = 120) => {
    const t0 = performance.now();
    for (let i = 0; i < n; i++) {
      state.tick(1 / 60);
      renderer.render(state, 1 / 60, null);
    }
    return (performance.now() - t0) / n;
  };
  if (bench) {
    const timer = setInterval(() => {
      if (state.objs.size < 10) return; // wait for the welcome to land
      clearInterval(timer);
      (window as any).tt.bench(Math.min(30, benchFrames)); // warm caches/shaders
      const ms = (window as any).tt.bench(benchFrames) as number;
      const out = document.createElement("div");
      out.id = "bench-result";
      out.style.cssText =
        "position:fixed;top:8px;left:8px;z-index:9999;background:#000;color:#0f0;font:16px monospace;padding:8px";
      out.textContent = `bench: ${ms.toFixed(2)} ms/frame over ${state.objs.size} objects`;
      document.body.append(out);
    }, 400);
  }

  ui.onModeToggle = () => {
    const next: Renderer = renderer.mode === "3d" ? new Render2D(settings) : new Render3D(settings);
    renderer.unmount();
    renderer = next;
    renderer.mount(gameEl);
    renderer.focusSeat(state.seat);
    ui.setMode(renderer.mode);
  };
  window.addEventListener("resize", () => renderer.resize());

  let gameState: { life: number[]; cmd: number[][] } | null = null;

  let last = performance.now();
  function frame(now: number): void {
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    state.tick(dt);
    input.tick(dt);
    renderer.render(state, dt, input.hoveredId);
    ui.update(state);
    if (gameState && gameState.life.length > 0) {
      ui.updateLifePanel(
        state, gameState.life, gameState.cmd,
        (seat, delta) => net.send({ t: "life", seat, delta }),
        (from, to, delta) => net.send({ t: "cmdDmg", from, to, delta }),
      );
    } else {
      ui.hideLifePanel();
    }
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
}

void boot();
